import React from "react";
import { useStoryItemEngineSql } from "../../../hooks/useStoryItemEngineSql";
import { storySqlDialogCopy as copy } from "./StorySqlDialog.copy";
import { Note, PaneBox, PaneButton, PaneCode, PaneHead, PaneHint, PaneTag, PaneTitle } from "./StorySqlDialog.styles";

interface StorySqlEnginePaneProps {
  storyId: string;
  chartId: string;
  copiedKey: string | null;
  onCopy: (sql: string, key: string) => void;
}

/** The SQL the engine ran for one chart, as it ran: read-only, with a copy button. Never rebuilt here. */
export const StorySqlEnginePane: React.FC<StorySqlEnginePaneProps> = ({ storyId, chartId, copiedKey, onCopy }) => {
  const engine = useStoryItemEngineSql(storyId, chartId, true);
  const statements = engine.data?.statements ?? [];
  return (
    <section
      aria-label={copy.enginePane}
      data-testid="story-sql-engine-pane"
      style={{ display: "flex", flexDirection: "column", gap: "0.75rem", minHeight: 0 }}
    >
      {engine.state === "error" ? <Note role="alert">{engine.errorMessage}</Note> : null}
      {engine.state === "loading" || engine.state === "idle" ? <Note role="status">{copy.loading}</Note> : null}
      {engine.state === "ready" && engine.data?.status === "unavailable" ? (
        <Note>{engine.data.message ?? copy.engineUnavailable}</Note>
      ) : null}
      {engine.state === "ready"
        ? statements.map((statement, index) => {
            const key = `${chartId}:engine:${index}`;
            return (
              <PaneBox key={key}>
                <PaneHead>
                  <PaneTag $engine>{copy.engineTag}</PaneTag>
                  <PaneTitle>{statement.output ? `${copy.enginePane}: ${statement.output}` : copy.enginePane}</PaneTitle>
                  <PaneHint>{copy.engineHint}</PaneHint>
                  <span style={{ flex: 1 }} />
                  {statement.sql ? (
                    <PaneButton type="button" onClick={() => onCopy(statement.sql as string, key)}>
                      {copiedKey === key ? copy.copied : copy.copy}
                    </PaneButton>
                  ) : null}
                </PaneHead>
                {statement.sql ? (
                  <PaneCode tabIndex={0} aria-label={copy.enginePane}>
                    {statement.sql}
                  </PaneCode>
                ) : (
                  <Note>{copy.engineExpired}</Note>
                )}
                {statement.truncated ? <Note>{copy.engineTruncated}</Note> : null}
              </PaneBox>
            );
          })
        : null}
    </section>
  );
};
